import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Tv, Film, Mic, Menu, User, LogOut, X, Users, BookOpen, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MobileNav({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/messages/unread-count`, { withCredentials: true, headers });
        setUnreadMessages(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail - non-critical
      }
    };
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);
  
  const interests = user?.interests || ['tv', 'movies', 'podcasts'];
  
  const navItems = [
    { id: 'social', label: 'Sorta Social', icon: Users, path: '/social', interest: null },
    { id: 'messages', label: 'Messages', icon: MessageSquare, path: '/messages', interest: null, showBadge: true },
    { id: 'tv', label: 'TV Shows', icon: Tv, path: '/tv-shows', interest: 'tv' },
    { id: 'movies', label: 'Films', icon: Film, path: '/films', interest: 'movies' },
    { id: 'podcasts', label: 'Podcasts', icon: Mic, path: '/podcasts', interest: 'podcasts' },
    { id: 'books', label: 'Books', icon: BookOpen, path: '/books', interest: 'books' },
  ].filter(item => item.interest === null || interests.includes(item.interest));
  
  const handleNavClick = (path) => {
    navigate(path);
    setOpen(false);
  };
  
  const handleLogout = () => {
    setOpen(false);
    onLogout();
  };
  
  const getDisplayName = () => user?.display_name || user?.name || 'User';
  const getInitial = () => getDisplayName().charAt(0).toUpperCase();
  
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="sm:hidden text-white hover:bg-white/10"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] bg-[#3D4F6F] border-r-0 p-0">
        <SheetHeader className="p-6 border-b border-white/10">
          <SheetTitle className="text-white font-outfit flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
              alt="Sorta"
              className="h-10 object-contain rounded-lg"
            />
            <span>Media</span>
          </SheetTitle>
        </SheetHeader>
        
        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 border-2 border-white/20">
              {user?.avatar_url ? (
                <AvatarImage src={user.avatar_url} alt={getDisplayName()} />
              ) : user?.picture ? (
                <AvatarImage src={user.picture} alt={getDisplayName()} />
              ) : (
                <AvatarFallback className="bg-[#F87171] text-white font-bold">
                  {getInitial()}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{getDisplayName()}</p>
              <p className="text-white/60 text-sm truncate">{user?.email}</p>
            </div>
          </div>
        </div>
        
        {/* Navigation Links */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
                isActive(item.path)
                  ? 'bg-[#F87171] text-white'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`mobile-nav-${item.id}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.label}</span>
              {item.showBadge && unreadMessages > 0 && (
                <Badge className="ml-auto bg-yellow-400 text-yellow-900 text-xs px-1.5 min-w-[20px] h-[20px] flex items-center justify-center">
                  {unreadMessages > 9 ? '9+' : unreadMessages}
                </Badge>
              )}
            </button>
          ))}
        </nav>
        
        {/* Bottom Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 space-y-2">
          <button
            onClick={() => handleNavClick('/profile')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all"
            data-testid="mobile-nav-profile"
          >
            <User className="h-5 w-5" />
            <span className="font-medium">Profile</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all"
            data-testid="mobile-nav-logout"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
